// A content calendar's posts. A post is an entry (shared/entries.ts) on a calendar board: a day and a time,
// a channel, a status, its text (a thread is several parts), where it came from, and once it went out, where.
// Planned and drafted here, posted by the user: nothing here ever publishes. Shared by the server (the agent's
// tools, read_board) and the browser (the month, the post panel, the phone's agenda, Download as Markdown).

import { IMAGE_KEY } from "./imageInfo";
import type { Entry } from "./types";

export const POST_STATUSES = ["idea", "draft", "scheduled", "posted"] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

export const STATUS_NAME: Record<PostStatus, string> = { idea: "Idea", draft: "Draft", scheduled: "Scheduled", posted: "Posted" };

/** The channels a post can be for out of the box. Any other is a name the user gives (Threads, YouTube). */
export interface Channel {
  id: string;
  name: string;
  /** the most characters one post (or one part of a thread) can have there, when there is a limit */
  limit: number | null;
}

export const CHANNELS: Channel[] = [
  { id: "x", name: "X", limit: 280 },
  { id: "linkedin", name: "LinkedIn", limit: 3000 },
  { id: "newsletter", name: "Newsletter", limit: null },
  { id: "blog", name: "Blog", limit: null },
];

const ALIASES: Record<string, string> = { x: "x", twitter: "x", "x (twitter)": "x", "twitter/x": "x", linkedin: "linkedin", "linked in": "linkedin", newsletter: "newsletter", email: "newsletter", blog: "blog" };

export const MAX_TITLE = 200;
export const MAX_PARTS = 25;
export const MAX_PART = 25_000;
export const MAX_FROM = 200;
export const MAX_CHANNEL = 40;
/** pictures on one post, as many as X takes on one */
export const MAX_POST_IMAGES = 4;

/** A picture on a post, kept by the board (/api/img/<key>), shrunk in the browser before it was sent. */
export interface PostImage {
  key: string;
  w: number;
  h: number;
}

export interface PostData {
  /** a short name for the post, as the calendar shows it; "" for none, when its first words show */
  title: string;
  /** the day it is planned for (YYYY-MM-DD, the user's own date), or null for an idea not on a day yet */
  day: string | null;
  /** its time that day (HH:MM, 24 hours), or null */
  time: string | null;
  /** "x", "linkedin", "newsletter", "blog", another channel's name, or null when none is chosen yet */
  channel: string | null;
  status: PostStatus;
  /** its text: one part for a post, several for a thread */
  parts: string[];
  /** where it came from, in words: a map, a page, a talk */
  from: string;
  /** where it went out, once posted: a link to the post */
  link: string | null;
  /** when it was marked posted */
  postedAt: number | null;
  /** its pictures, in order, for planning and copying out; [] for none */
  images: PostImage[];
}

type Refuse = (status: number, message: string) => never;

/** A channel as it is kept: one of the four by its id (X, Twitter, LinkedIn, Email...), else the name given. */
export function channelId(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const t = raw.replace(/\s+/g, " ").trim();
  if (!t) return null;
  return ALIASES[t.toLowerCase()] ?? t.slice(0, MAX_CHANNEL);
}

export function channelOf(id: string | null): Channel | null {
  if (!id) return null;
  return CHANNELS.find((c) => c.id === id) ?? { id, name: id, limit: null };
}

export function channelName(id: string | null): string {
  return channelOf(id)?.name ?? "No channel";
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real date, as YYYY-MM-DD, or null. */
export function cleanDay(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const m = DAY.exec(raw.trim());
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3] ? raw.trim() : null;
}

/** A time of day as HH:MM (9:30, 09:30 and 9:30 pm all read), or null. */
export function cleanTime(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const m = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/i.exec(raw.trim());
  if (!m) return null;
  let h = +m[1];
  const min = m[2] ? +m[2] : 0;
  const ap = m[3]?.[0].toLowerCase();
  if (ap) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (ap === "p" ? 12 : 0);
  }
  if (h > 23 || min > 59 || (!m[2] && !ap)) return null;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

export function isPostStatus(x: unknown): x is PostStatus {
  return typeof x === "string" && (POST_STATUSES as readonly string[]).includes(x);
}

/** A link to where a post went out: http or https only. */
export function cleanLink(raw: unknown): string | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    const u = new URL(raw.trim());
    return u.protocol === "https:" || u.protocol === "http:" ? u.toString().slice(0, 500) : null;
  } catch {
    return null;
  }
}

/** A post's fields as a calendar keeps them: each one given is checked, each one left out stays as it was. */
export function cleanPost(raw: Record<string, unknown>, before: PostData | null, refuse: Refuse): PostData {
  const was: PostData = before ?? { title: "", day: null, time: null, channel: null, status: "idea", parts: [""], from: "", link: null, postedAt: null, images: [] };
  const has = (k: string) => Object.hasOwn(raw, k);
  const title = has("title") ? (typeof raw.title === "string" ? raw.title.replace(/[\r\n\t]+/g, " ").slice(0, MAX_TITLE) : "") : was.title;
  let day = was.day;
  if (has("day")) {
    day = raw.day === null || raw.day === "" ? null : cleanDay(raw.day);
    if (raw.day !== null && raw.day !== "" && !day) refuse(400, "A post's day is a date like 2026-10-15, or null for an idea with no day.");
  }
  let time = was.time;
  if (has("time")) {
    time = raw.time === null || raw.time === "" ? null : cleanTime(raw.time);
    if (raw.time !== null && raw.time !== "" && !time) refuse(400, "A post's time is a time of day like 09:30 or 14:00, or null for none.");
  }
  const channel = has("channel") ? channelId(raw.channel) : was.channel;
  let status = was.status;
  if (has("status")) {
    if (!isPostStatus(raw.status)) refuse(400, `A post's status is one of ${POST_STATUSES.join(", ")}.`);
    status = raw.status as PostStatus;
  }
  let parts = was.parts;
  if (has("parts")) {
    if (!Array.isArray(raw.parts) || !raw.parts.every((p) => typeof p === "string")) refuse(400, "A post's parts are its text as a list of strings: one for a post, several for a thread.");
    const list = raw.parts as string[];
    if (list.length > MAX_PARTS) refuse(400, `A thread holds up to ${MAX_PARTS} parts.`);
    parts = list.map((p) => p.slice(0, MAX_PART));
    if (!parts.length) parts = [""];
  }
  const from = has("from") ? (typeof raw.from === "string" ? raw.from.replace(/[\r\n\t]+/g, " ").trim().slice(0, MAX_FROM) : "") : was.from;
  let link = was.link;
  if (has("link")) {
    link = raw.link === null || raw.link === "" ? null : cleanLink(raw.link);
    if (raw.link !== null && raw.link !== "" && !link) refuse(400, "A post's link is the address where it went out, starting with https://.");
  }
  const postedAt = has("postedAt") ? (typeof raw.postedAt === "number" && Number.isFinite(raw.postedAt) ? raw.postedAt : null) : was.postedAt;
  let images = was.images;
  if (has("images")) {
    if (!Array.isArray(raw.images)) refuse(400, "A post's images are a list of pictures the board keeps.");
    const list = raw.images as unknown[];
    if (list.length > MAX_POST_IMAGES) refuse(400, `A post holds up to ${MAX_POST_IMAGES} pictures.`);
    images = list.map((x) => cleanImage(x) ?? refuse(400, "A post's picture is one the board keeps, added from the post."));
  }
  // an idea is a post with no day: one given a day is a draft, so a dated post never says Idea (Fixes 9)
  return { title, day, time, channel, status: day && status === "idea" ? "draft" : status, parts, from, link, postedAt, images };
}

/** A picture as a post keeps it: the board's key and its size, or null. */
function cleanImage(x: unknown): PostImage | null {
  if (!x || typeof x !== "object") return null;
  const { key, w, h } = x as Record<string, unknown>;
  const size = (n: unknown) => typeof n === "number" && Number.isInteger(n) && n > 0 && n <= 8192;
  return typeof key === "string" && IMAGE_KEY.test(key) && size(w) && size(h) ? { key, w: w as number, h: h as number } : null;
}

/** A post's data as it is kept, read safely from any entry (an old or odd one reads as an empty idea). */
export function postOf(e: Pick<Entry, "data">): PostData {
  try {
    return cleanPost(e.data ?? {}, null, () => {
      throw new Error("bad");
    });
  } catch {
    const d = e.data ?? {};
    return {
      title: typeof d.title === "string" ? d.title : "",
      day: cleanDay(d.day),
      time: cleanTime(d.time),
      channel: channelId(d.channel),
      status: isPostStatus(d.status) ? d.status : "idea",
      parts: Array.isArray(d.parts) ? d.parts.filter((p): p is string => typeof p === "string") : [""],
      from: typeof d.from === "string" ? d.from : "",
      link: cleanLink(d.link),
      postedAt: typeof d.postedAt === "number" ? d.postedAt : null,
      images: Array.isArray(d.images) ? d.images.map(cleanImage).filter((x): x is PostImage => !!x).slice(0, MAX_POST_IMAGES) : [],
    };
  }
}

/** What the calendar calls a post: its title, else its first words, else "Untitled post". */
export function postLabel(p: Pick<PostData, "title" | "parts">, max = 80): string {
  const t = p.title.trim();
  if (t) return t.length > max ? t.slice(0, max - 1).trimEnd() + "…" : t;
  const first = (p.parts.find((x) => x.trim()) ?? "").replace(/\s+/g, " ").trim();
  if (!first) return "Untitled post";
  return first.length > max ? first.slice(0, max - 1).trimEnd() + "…" : first;
}

// ---------------------------------------------------------------------------
// counting characters as each channel does

// X counts a link as 23 characters, an emoji as 2, and most other scripts' characters as 1 (twitter-text's
// weighted length); CJK and the like count 2.
const URL_RE = /\bhttps?:\/\/[^\s]+|\b(?:[a-z0-9-]+\.)+(?:com|org|net|io|dev|co|app|ai|xyz|me|so)(?:\/[^\s]*)?/gi;
const LIGHT: [number, number][] = [
  [0, 4351],
  [8192, 8205],
  [8208, 8223],
  [8242, 8247],
];
const PICTO = /\p{Extended_Pictographic}/u;

function weighted(text: string): number {
  let n = 0;
  const seg = new Intl.Segmenter(undefined, { granularity: "grapheme" });
  for (const { segment } of seg.segment(text)) {
    if (PICTO.test(segment)) {
      n += 2;
      continue;
    }
    for (const ch of segment) {
      const c = ch.codePointAt(0)!;
      n += LIGHT.some(([a, b]) => c >= a && c <= b) ? 1 : 2;
    }
  }
  return n;
}

/** How long a part is where it is going: as X counts it for X, else in characters as a person counts them. */
export function charCount(text: string, channel: string | null): number {
  if (channel === "x") {
    let n = 0;
    const rest = text.replace(URL_RE, () => {
      n += 23;
      return "";
    });
    return n + weighted(rest);
  }
  let n = 0;
  for (const _ of new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(text)) n++;
  return n;
}

/**
 * The parts of a post longer than its channel takes, each by its number (from 1) and how far over (Fixes 9, 28 Oct: a
 * 300 character X post was kept with no word, and the agent took it as ready). Saved all the same: the user may cut it.
 */
export function overLimit(post: Pick<PostData, "channel" | "parts">, xPremium = false): { part: number; over: number; limit: number }[] {
  const limit = channelOf(post.channel)?.limit;
  // X Premium posts can run longer than 280: no limit to be over (Fixes 10)
  if (!limit || (xPremium && post.channel === "x")) return [];
  return post.parts.flatMap((text, i) => {
    const n = charCount(text, post.channel);
    return n > limit ? [{ part: i + 1, over: n - limit, limit }] : [];
  });
}

/** The same, short, for a post's line in a list: "over X's 280 (part 2 by 20)". */
export function overShort(post: Pick<PostData, "channel" | "parts">, xPremium = false): string | null {
  const over = overLimit(post, xPremium);
  if (!over.length) return null;
  const by = post.parts.length === 1 ? `by ${over[0].over}` : over.map((o) => `part ${o.part} by ${o.over}`).join(", ");
  return `over ${channelName(post.channel)}'s ${over[0].limit.toLocaleString("en-US")} (${by})`;
}

/** Those parts in words: "Part 2 is 20 characters over X's 280", or the post's own for one alone. */
export function overWords(post: Pick<PostData, "channel" | "parts">, xPremium = false): string | null {
  const over = overLimit(post, xPremium);
  if (!over.length) return null;
  const name = channelName(post.channel);
  const one = post.parts.length === 1;
  const each = over.map((o) => `${one ? "It is" : `Part ${o.part} is`} ${o.over.toLocaleString("en-US")} character${o.over === 1 ? "" : "s"} over`);
  return `${each.join("; ")} ${name}'s ${over[0].limit.toLocaleString("en-US")}.`;
}

// ---------------------------------------------------------------------------
// days

export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** A day key as a local date at noon, so adding days never trips over a clock change. */
export function dayDate(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d, 12);
}

export function addDays(key: string, n: number): string {
  const d = dayDate(key);
  d.setDate(d.getDate() + n);
  return dayKey(d);
}

/** The Monday of a day's week (weeks start on Monday, as the calendar draws them). */
export function weekStart(key: string): string {
  const d = dayDate(key);
  return addDays(key, -((d.getDay() + 6) % 7));
}

/** "2026-10" for a day key. */
export const monthOf = (key: string) => key.slice(0, 7);

/** The days a month's grid shows: from the Monday on or before its first day to the Sunday on or after its last. */
export function monthGrid(month: string): string[] {
  const first = `${month}-01`;
  const start = weekStart(first);
  const d = dayDate(first);
  const last = dayKey(new Date(d.getFullYear(), d.getMonth() + 1, 0, 12));
  const end = addDays(weekStart(last), 6);
  const out: string[] = [];
  for (let k = start; k <= end; k = addDays(k, 1)) out.push(k);
  return out;
}

export function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1, 12);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** "October 2026" */
export function monthName(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

/** "Thu, Oct 15" or, long, "Thursday, October 15" */
export function dayName(key: string, long = false): string {
  const d = dayDate(key);
  const wd = DAYS[d.getDay()];
  const mo = MONTHS[d.getMonth()];
  return long ? `${wd}, ${mo} ${d.getDate()}` : `${wd.slice(0, 3)}, ${mo.slice(0, 3)} ${d.getDate()}`;
}

/** "9:30 AM" for "09:30" */
export function timeName(t: string | null): string {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

/** Posts in the order a day shows them: timed ones by time, then the rest by their place. */
export function byTime<E extends Pick<Entry, "order" | "createdAt" | "id"> & { post: PostData }>(list: E[]): E[] {
  return [...list].sort((a, b) => (a.post.time ?? "99").localeCompare(b.post.time ?? "99") || a.order - b.order || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
}

// ---------------------------------------------------------------------------
// the posts as Markdown: Download as Markdown, and what an agent reads

/** One post as Markdown: a heading with its time, channel, status and name, then where it came from and its text. */
export function postMarkdown(p: PostData, opts: { id?: string; level?: number; day?: boolean } = {}): string {
  const h = "#".repeat(opts.level ?? 3);
  const head = [opts.day && p.day ? dayName(p.day) : "", p.time ? timeName(p.time) : "", channelName(p.channel), STATUS_NAME[p.status]].filter(Boolean).join(" · ");
  const lines = [`${h} ${head}: ${postLabel(p, 120)}${opts.id ? ` (post id ${opts.id})` : ""}`];
  const meta = [p.from ? `From: ${p.from}` : "", p.link ? `Posted at: ${p.link}` : "", p.images.length ? `Pictures: ${p.images.length} (${p.images.map((i, n) => `${n + 1}: ${i.w}×${i.h} at /api/img/${i.key}`).join(", ")})` : ""].filter(Boolean);
  if (meta.length) lines.push("", ...meta);
  const parts = p.parts.filter((x) => x.trim());
  if (!parts.length) lines.push("", "(No text yet.)");
  else if (parts.length === 1) lines.push("", parts[0].trim());
  else parts.forEach((x, i) => lines.push("", `**${i + 1}/${parts.length}**`, "", x.trim()));
  return lines.join("\n");
}

/** A month of posts as a Markdown file: each day with posts, its posts in order, then the ideas with no day. */
export function monthMarkdown(boardName: string, month: string, posts: (Entry & { post: PostData })[], ideas: (Entry & { post: PostData })[] = []): string {
  const out = [`# ${boardName}: ${monthName(month)}`];
  const inMonth = posts.filter((e) => e.post.day && monthOf(e.post.day) === month);
  const days = [...new Set(inMonth.map((e) => e.post.day!))].sort();
  if (!days.length) out.push("", "No posts planned this month.");
  for (const d of days) {
    out.push("", `## ${dayName(d, true)}`);
    for (const e of byTime(inMonth.filter((x) => x.post.day === d))) out.push("", postMarkdown(e.post));
  }
  if (ideas.length) {
    out.push("", "## Ideas with no day yet");
    for (const e of ideas) out.push("", postMarkdown(e.post));
  }
  return out.join("\n") + "\n";
}
